import { Search } from 'lucide-react'
import type { ReactNode } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { Tone } from '../types'
import { TONE_DOT } from '../ui'

export function Toolbar({ q, onQ, placeholder, count, children }: { q: string; onQ: (v: string) => void; placeholder: string; count?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b p-3">
      <div className="relative w-full sm:max-w-xs">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input value={q} onChange={(e) => onQ(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="pl-8" />
      </div>
      {children}
      {count != null ? <span className="ml-auto text-xs text-muted-foreground">{count}</span> : null}
    </div>
  )
}

export function UsageBar({ value, label, warn = 70, danger = 85, className }: { value: number; label?: ReactNode; warn?: number; danger?: number; className?: string }) {
  const tone = value >= danger ? 'bg-destructive' : value >= warn ? 'bg-warning' : 'bg-success'
  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      {label ? (
        <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className="truncate">{label}</span>
          <span className="font-medium tabular-nums text-foreground">%{Math.round(value)}</span>
        </div>
      ) : null}
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
        <div className={cn('h-full rounded-full transition-all', tone)} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
    </div>
  )
}

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'border-primary bg-primary' : 'border-input bg-muted',
      )}
    >
      <span className={cn('inline-block size-3.5 rounded-full bg-white shadow-sm transition-transform', checked ? 'translate-x-[18px]' : 'translate-x-0.5')} />
    </button>
  )
}

export function Dot({ tone, className }: { tone: Tone; className?: string }) {
  return <span aria-hidden className={cn('inline-block size-2 shrink-0 rounded-full', TONE_DOT[tone], className)} />
}

export function MetaList({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="divide-y text-sm">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-baseline justify-between gap-3 py-2">
          <dt className="shrink-0 text-muted-foreground">{k}</dt>
          <dd className="min-w-0 text-right font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
